import classNames from "classnames";
import Datasource, { DatasourceResponse } from "lib/datasource";
import moment from "moment";
import React, { memo, useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import { Button, LoadingAnimation, ModalHeader } from "components/Shared";

// ----------------------------------------------------------------------------
// Delete Form warning modal — styled like components/Tasks/Override.tsx's
// orange "warning" header, but with no comment field (unlike Override).
// Modeled on components/Projects/Trash.tsx for the Datasource delete pattern.
//
// Contract: DELETE /v2/forms/:id, empty body. 204 => success; the backend
// cascades removal of any template workflow conditions that reference this
// form. See api-contract.md.
// ----------------------------------------------------------------------------

interface DeleteModalProps {
	isOpen: boolean;
	onRequestClose: () => void;
	formId: string;
	onDeleteSuccess: () => void;
}

const DeleteModal = ({ isOpen, onRequestClose, formId, onDeleteSuccess }: DeleteModalProps) => {
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(new Datasource({ mainModelName: "form", renderTrigger }));
	const [error, setError] = useState<string | null>(null);

	const deleteForm = useCallback(() => {
		// Guard against a double-submit while the request is in flight (the button
		// also becomes non-interactive via its loading state, see _modal.scss).
		if (datasource.isLoading) return;
		setError(null);
		datasource.del("v2", `forms/${formId}`, {}, { updateIsLoding: true });
	}, [datasource, formId]);

	useEffect(() => {
		const sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			if (response.success) {
				setError(null);
				onRequestClose();
				onDeleteSuccess();
			} else {
				setError("Something went wrong while deleting this form. Please try again.");
			}
		});
		return () => sub.unsubscribe();
	}, [datasource, onRequestClose, onDeleteSuccess]);

	return (
		<Modal
			ariaHideApp={false}
			isOpen={isOpen}
			className={classNames("warning", "delete-form-modal")}
			onRequestClose={onRequestClose}
			contentLabel="delete-form"
		>
			<ModalHeader title="delete_form" onRequestClose={onRequestClose} />
			<section>
				<p>
					You are about to delete this form. Any condition that uses this form will also be removed from the
					corresponding template. This action cannot be undone.
				</p>
				{error && <div className="alert alert-danger">{error}</div>}
			</section>
			<footer>
				{/* Button internally calls translate(children || kind) once — pass the
				    raw i18n key here, not a pre-translated string (mirrors how
				    Header.tsx passes `button.label` through untranslated). */}
				<Button onClick={onRequestClose}>cancel</Button>
				{/* While the request is in flight, keep the SAME red button (same size,
				    same label) and overlay the branded TCP spinner (LoadingAnimation)
				    inside it — no layout shift, no oversized standalone spinner. The
				    `loading` class hides the label (see _modal.scss) so the spinner
				    sits centered without the button resizing. `disabled` is
				    intentionally NOT used because the shared button's disabled style
				    desaturates/blurs it, which would drop the red. */}
				<span className="delete-action" style={{ position: "relative", display: "inline-flex" }}>
					<Button kind="destroy" onClick={deleteForm} loading={datasource.isLoading} />
					{datasource.isLoading && (
						// Overlay the branded TCP spinner. Layout is done with INLINE
						// styles (position:absolute so it can NEVER affect the button's
						// size — no stretch) and it reuses the existing `.loading` class
						// only for the spinner's colours + rotation. Scaled down since the
						// shared loader svg is ~96px.
						<span
							className="loading delete-spinner"
							aria-hidden="true"
							style={{
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%) scale(0.24)",
								padding: 0,
								margin: 0,
								maxHeight: "none",
								pointerEvents: "none"
							}}
						>
							<LoadingAnimation />
						</span>
					)}
				</span>
			</footer>
		</Modal>
	);
};

export default memo(DeleteModal);
